import { toRaw, isReactive } from 'vue';

type DeepObject<T> = {
    [K in keyof T]: T[K] extends object ? DeepObject<T[K]> : T[K];
};

interface Characteristic {
    id?: string;
    [key: string]: any; // Allow other properties
}

export function removeTemporaryIds(
    dataArray: Characteristic[] | undefined,
    idPrefix: string,
): void {
    if (!Array.isArray(dataArray)) {
        console.error('Expected an array but received:', typeof dataArray);
        return;
    }

    try {
        dataArray.forEach((item) => {
            if (typeof item !== 'object' || item === null) {
                console.warn('Invalid item encountered in array:', item);
                return;
            }

            if (item.id?.startsWith(idPrefix)) {
                delete item.id;
            }
        });
    } catch (error) {
        console.error('Error removing temporary IDs:', error);
    }
}

function deepToRaw<T>(obj: T): DeepObject<T> {
    if (Array.isArray(obj)) {
        return obj.map((item) => deepToRaw(item)) as DeepObject<T>;
    } else if (obj && typeof obj === 'object') {
        const rawObj: Partial<DeepObject<T>> = {};
        Object.keys(obj).forEach((key) => {
            const value = (obj as any)[key];
            rawObj[key as keyof DeepObject<T>] = isReactive(value)
                ? deepToRaw(toRaw(value))
                : deepToRaw(value);
        });
        return rawObj as DeepObject<T>;
    }
    return obj as DeepObject<T>;
}

export function deepCloneRawIfValid<T>(obj: T): T {
    if (isWindow(obj)) {
        throw new Error('Failed to clone object: object is window');
    }
    if (isEmptyObject(obj)) {
        return {} as T;
    }

    let rawObj;
    try {
        rawObj = deepToRaw(obj);
        const clonedObj = structuredClone(rawObj);
        return clonedObj;
    } catch (error) {
        console.error('Failed to clone object', error);
        throw new Error('Failed to clone object', { cause: error });
    }
}

function isEmptyObject(obj) {
    return Object.keys(obj).length === 0 && obj.constructor === Object;
}

function isWindow(obj) {
    return obj !== null && obj === window;
}
